import React, { Component, ErrorInfo, ReactNode } from 'react';
import { reportError } from '../utils/telemetry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  pageName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, componentStack: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { pageName } = this.props;
    console.error(
      `ErrorBoundary caught${pageName ? ` on [${pageName}]` : ''}:`,
      error,
      errorInfo.componentStack,
    );
    this.setState({ componentStack: errorInfo.componentStack ?? null });
    reportError(error, { pageName });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const { pageName } = this.props;

      return (
        <div
          role="alert"
          className="flex flex-col items-center justify-center min-h-[calc(100vh-72px)] px-4 text-center"
        >
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {pageName
              ? `${pageName} 페이지에서 문제가 발생했습니다`
              : '문제가 발생했습니다'}
          </h2>
          <p className="text-sm text-gray-600 mb-6 max-w-md">
            {pageName
              ? '다른 페이지는 정상 작동합니다. 아래 버튼으로 이동하세요.'
              : '예상치 못한 오류가 발생했습니다. 페이지를 새로고침하거나 홈으로 돌아가주세요.'}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => (window.location.href = '/')}
              aria-label="홈으로 이동"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              홈으로
            </button>
            <button
              onClick={() => window.location.reload()}
              aria-label="페이지 새로고침"
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              새로고침
            </button>
          </div>
          {import.meta.env.DEV && this.state.error && (
            <pre className="mt-6 p-4 bg-red-50 text-red-700 text-xs rounded-lg max-w-lg overflow-auto text-left">
              {this.state.error.message}
              {this.state.componentStack && `\n\nComponent Stack:${this.state.componentStack}`}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
